// ============================================================================
// ui/shell/zoom-popover.js — zoom in/out/fit/fullscreen slide-out (reusable shell)
// ============================================================================
// Ports the Gym Designer's `.zoom-popover` (dark): a cluster that expands
// leftward from a zoom tool button — zoom out · % readout · zoom in · fit-to-
// view · fullscreen. Rotate is intentionally OMITTED (not meaningful for a
// single centered object). Purely a view of callbacks + a percent readout; it
// owns no zoom state itself.
//
//   const zoom = createZoomPopover({ onZoomOut, onZoomIn, onFit, onFullscreen, getPercent });
//   zoomBtn.addEventListener('click', () => zoomBtn.classList.toggle('is-active', zoom.toggle()));
// ============================================================================

import { h } from '../dom.js';

const ICON_OUT =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12h14"/></svg>';
const ICON_IN =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3"><path d="M12 5v14M5 12h14"/></svg>';
const ICON_FIT =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>';
const ICON_FS =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5">' +
  '<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>';

/**
 * @param {object} config
 * @param {()=>void} config.onZoomOut
 * @param {()=>void} config.onZoomIn
 * @param {()=>void} config.onFit
 * @param {()=>void} [config.onFullscreen]        - omit to hide the fullscreen button.
 * @param {()=>number} [config.getPercent]        - returns the current zoom %, for the readout.
 * @returns {{ el:HTMLElement, toggle:()=>boolean, show:()=>void, hide:()=>void,
 *            isVisible:()=>boolean, updatePercent:()=>void }}
 */
export function createZoomPopover(config) {
  const { onZoomOut, onZoomIn, onFit, onFullscreen, getPercent } = config;

  const level = h('span', { class: 'ds-zoom-level', 'aria-live': 'polite' }, '100%');

  const zbtn = (icon, title, handler, extraClass) =>
    h(
      'button',
      {
        type: 'button',
        class: 'ds-zoom-btn wd-flat-glass wd-flat-glass--flush' + (extraClass ? ' ' + extraClass : ''),
        'aria-label': title,
        title,
        onClick: () => {
          handler();
          updatePercent();
        },
      },
      h('span', { 'aria-hidden': 'true', html: icon }),
    );

  const row = h(
    'div',
    { class: 'ds-zoom-row' },
    zbtn(ICON_OUT, 'Zoom out', onZoomOut),
    level,
    zbtn(ICON_IN, 'Zoom in', onZoomIn),
    zbtn(ICON_FIT, 'Fit to view', onFit),
    typeof onFullscreen === 'function' ? zbtn(ICON_FS, 'Fullscreen', onFullscreen, 'ds-zoom-btn--fs') : null,
  );

  const el = h(
    'div',
    { class: 'ds-zoom-popover wd-flat-glass', role: 'group', 'aria-label': 'Zoom controls' },
    h('div', { class: 'ds-zoom-popover-title' }, 'Zoom'),
    row,
  );

  function updatePercent() {
    if (typeof getPercent !== 'function') return;
    const pct = getPercent();
    if (typeof pct === 'number' && isFinite(pct)) level.textContent = `${Math.round(pct)}%`;
  }
  function show() {
    el.classList.add('is-visible');
    updatePercent();
  }
  function hide() {
    el.classList.remove('is-visible');
  }
  function isVisible() {
    return el.classList.contains('is-visible');
  }
  function toggle() {
    if (isVisible()) hide();
    else show();
    return isVisible();
  }

  return { el, toggle, show, hide, isVisible, updatePercent };
}
